import { Box } from '@mui/material';
import { Layout } from './components/layout/Layout.tsx';
import { useEffect, useMemo, useState } from 'react';
import { Outlet, ScrollRestoration } from 'react-router-dom';
import { ROLES, useGetMeQuery } from './redux/api/user.api.ts';
import { useActiveNavigation } from './hooks/useActiveNavigation.ts';
import UserProfileMenu from './components/UserProfileMenu';
import { injectThemeVariables } from './utilities/theme.ts';
import { isAuthorized } from './utilities/auth.ts';
import { useEmployeePhotoById } from './hooks/useEmployeePhotoById.ts';
import { useFeatureFlags } from './hooks/useFeatureFlags.ts';
import { LibContext } from 'framework-react-core';

export function Root() {
  useFeatureFlags();
  const { data } = useGetMeQuery();
  const me = data?.data;
  const profilePhotoUrl = useEmployeePhotoById(me?.oid);
  const [pageTitle, setPageTitle] = useState('Conflicts of Interest Search Requests');

  const navLinks = useMemo(() => {
    return [
      isAuthorized(data?.data, [ROLES.ADMIN, ROLES.VIEW_ONLY, ROLES.USER, ROLES.REVIEWER, ROLES.QL])
        ? {
            label: 'My Forms',
            path: '/myforms',
            activePaths: ['/', '/myforms'],
          }
        : null,
      isAuthorized(data?.data, [ROLES.ADMIN, ROLES.VIEW_ONLY, ROLES.USER, ROLES.REVIEWER, ROLES.QL])
        ? { label: 'All Forms', path: '/allforms', activePaths: ['/allforms'] }
        : null,
      isAuthorized(data?.data, [ROLES.ADMIN, ROLES.REVIEWER, ROLES.QL])
        ? {
            label: 'My Approvals',
            path: '/myapprovals',
            activePaths: ['/myapprovals'],
          }
        : null,
      isAuthorized(data?.data, [ROLES.ADMIN, ROLES.REVIEWER, ROLES.QL])
        ? {
            label: 'All Approvals',
            path: '/allapprovals',
            activePaths: ['/allapprovals'],
          }
        : null,
      isAuthorized(data?.data, [ROLES.ADMIN, ROLES.VIEW_ONLY, ROLES.USER, ROLES.REVIEWER, ROLES.QL])
        ? {
            label: 'Quality & Risk',
            path: '/qualityandrisk',
            activePaths: ['/qualityandrisk'],
          }
        : null,
      isAuthorized(data?.data, [ROLES.ADMIN])
        ? {
            label: 'Admin View',
            path: '#',
            activePaths: ['/adminview'],
          }
        : null,
    ].filter((t) => t !== null);
  }, [data?.data]);

  // Inject CSS variables from theme.ts on mount
  useEffect(() => {
    injectThemeVariables();
  }, []);

  // Configure navigation active state tracking
  useActiveNavigation(
    navLinks.map(({ label, activePaths }) => ({
      label,
      paths: activePaths,
    }))
  );

  return (
    <LibContext provideRouter={false}>
      <UserProfileMenu name={me?.name} avatarUrl={profilePhotoUrl} />
      <ScrollRestoration getKey={({ pathname }) => pathname} />
      <Layout title={pageTitle} menuOptions={navLinks}>
        <Box minHeight="calc(100vh - 64px)" py={4} px={2} bgcolor="var(--color-background-default)">
          <Outlet context={{ setPageTitle }} />
        </Box>
      </Layout>
    </LibContext>
  );
}
